import { useState } from "react";
import { GraduationCap, Users, Presentation, BadgePoundSterling, Send, Check } from "lucide-react";
import { toast } from "sonner";
import { Reveal, MaskedLine } from "@/components/Reveal";
import api, { formatApiError } from "@/lib/api";

const OFFERS = [
  {
    icon: Presentation,
    title: "CV Masterclass Workshop",
    price: "From £12 / student",
    text: "A live 90-minute session for your cohort: UK two-page CV rules, ATS proofing, and translating foreign degrees into recruiter-friendly British terms. Every attendee leaves with a rewritten professional summary.",
  },
  {
    icon: Users,
    title: "Interview Skills Bootcamp",
    price: "From £15 / student",
    text: "Half-day intensive covering STAR methodology, competency rubrics used by UK graduate schemes, and live mock interviews with feedback from our UK HR team.",
  },
  {
    icon: GraduationCap,
    title: "Graduate Visa Career Pack",
    price: "Custom cohort pricing",
    text: "Bulk CV + LinkedIn packages for final-year international students, timed around the Graduate visa window — including sponsorship-ready positioning and licensed-sponsor job lists.",
  },
];

const ORG_TYPES = ["University Careers Service", "Student Society / Union", "International Student Office", "Department / Faculty", "Private Education Provider", "Other"];
const WORKSHOP_TYPES = ["CV Masterclass Workshop", "Interview Skills Bootcamp", "Graduate Visa Career Pack", "Full series (all three)", "Custom programme"];

export default function Partnerships() {
  const [form, setForm] = useState({ org_name: "", contact_name: "", email: "", phone: "", org_type: ORG_TYPES[0], students_count: "", workshop_type: WORKSHOP_TYPES[0], message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/partnerships", form);
      setSent(true);
      toast.success("Request received — we'll reply with a tailored quote within 2 UK working days.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="partnerships-page">
      <section className="mx-auto max-w-7xl px-5 pb-16 pt-20 lg:px-8 lg:pt-28">
        <span className="overline-label">University Partnerships</span>
        <h1 className="mt-5 max-w-3xl font-serif text-4xl font-semibold leading-tight text-slate-50 sm:text-5xl lg:text-6xl" data-testid="partnerships-headline">
          <MaskedLine delay={0.1}>Bring UK hiring expertise</MaskedLine>
          <MaskedLine delay={0.3} className="italic text-[#C5A059]">to your students.</MaskedLine>
        </h1>
        <Reveal delay={0.5}>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
            Paid bulk workshops and cohort packages for university careers services, student societies and international offices — delivered live by UK HR professionals, online or in person.
          </p>
        </Reveal>
      </section>

      {/* OFFERS */}
      <section className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid gap-6 md:grid-cols-3">
          {OFFERS.map((o, i) => (
            <Reveal key={o.title} delay={i * 0.08}>
              <div className="card-surface flex h-full flex-col p-8 transition-colors duration-300 hover:border-[#C5A059]/50" data-testid={`offer-card-${i}`}>
                <o.icon size={26} className="text-[#C5A059]" />
                <h3 className="mt-5 font-serif text-2xl font-semibold text-slate-50">{o.title}</h3>
                <span className="mt-2 inline-flex items-center gap-1.5 font-mono text-xs font-semibold uppercase tracking-[0.12em] text-emerald-400">
                  <BadgePoundSterling size={13} /> {o.price}
                </span>
                <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-400">{o.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHY */}
      <section className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
        <Reveal>
          <div className="card-surface grid gap-8 p-8 lg:grid-cols-2 lg:p-10">
            <div>
              <span className="overline-label">Why partner with RMS</span>
              <h2 className="mt-3 font-serif text-3xl font-semibold text-slate-50">Insider expertise your students can't Google</h2>
              <p className="mt-4 text-sm leading-relaxed text-slate-400">
                International students face the steepest odds in the UK job market — ATS screens, sponsorship filters and unwritten British CV rules. Our workshops are delivered by someone who sat on the other side of that table, scoring real applications for UK employers.
              </p>
            </div>
            <ul className="space-y-4 self-center">
              {["Live delivery online (any timezone) or on campus across the UK", "Workshop slides and CV templates your students keep", "Cohort pricing with volume discounts from 20+ students", "Follow-up office hours for individual student questions"].map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm text-slate-300">
                  <Check size={16} className="mt-0.5 shrink-0 text-[#C5A059]" /> {t}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </section>

      {/* FORM */}
      <section className="mx-auto max-w-4xl px-5 pb-24 lg:px-8">
        <Reveal>
          <div className="card-surface gold-stroke p-8 lg:p-10">
            {sent ? (
              <div className="py-8 text-center" data-testid="partnership-success">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15">
                  <Send size={22} className="text-emerald-400" />
                </div>
                <h2 className="mt-6 font-serif text-2xl font-semibold text-slate-50">Request received</h2>
                <p className="mx-auto mt-3 max-w-md text-sm text-slate-400">
                  Thank you, {form.contact_name.split(" ")[0]}. We'll review your cohort needs and reply to {form.email} with a tailored quote within 2 UK working days.
                </p>
              </div>
            ) : (
              <>
                <h2 className="font-serif text-3xl font-semibold text-slate-50" data-testid="partnership-form-title">Request a bulk workshop quote</h2>
                <p className="mt-3 text-sm text-slate-400">Tell us about your cohort and we'll come back with tailored pricing — no obligation.</p>
                <form onSubmit={submit} className="mt-8 space-y-6" data-testid="partnership-form">
                  <div className="grid gap-6 sm:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400" htmlFor="p-org">Organisation / University *</label>
                      <input id="p-org" data-testid="partnership-org-input" required value={form.org_name} onChange={set("org_name")} className="input-dark" placeholder="e.g. University of Manchester ISOC" />
                    </div>
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400" htmlFor="p-name">Your Name *</label>
                      <input id="p-name" data-testid="partnership-name-input" required value={form.contact_name} onChange={set("contact_name")} className="input-dark" placeholder="Full name" />
                    </div>
                  </div>
                  <div className="grid gap-6 sm:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400" htmlFor="p-email">Work Email *</label>
                      <input id="p-email" data-testid="partnership-email-input" required type="email" value={form.email} onChange={set("email")} className="input-dark" placeholder="you@university.ac.uk" />
                    </div>
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400" htmlFor="p-phone">Phone</label>
                      <input id="p-phone" data-testid="partnership-phone-input" value={form.phone} onChange={set("phone")} className="input-dark" placeholder="+44 ..." />
                    </div>
                  </div>
                  <div className="grid gap-6 sm:grid-cols-3">
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400" htmlFor="p-type">Organisation Type</label>
                      <select id="p-type" data-testid="partnership-orgtype-select" value={form.org_type} onChange={set("org_type")} className="input-dark">
                        {ORG_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400" htmlFor="p-count">Estimated Students *</label>
                      <input id="p-count" data-testid="partnership-count-input" required value={form.students_count} onChange={set("students_count")} className="input-dark" placeholder="e.g. 40–60" />
                    </div>
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400" htmlFor="p-workshop">Workshop of Interest</label>
                      <select id="p-workshop" data-testid="partnership-workshop-select" value={form.workshop_type} onChange={set("workshop_type")} className="input-dark">
                        {WORKSHOP_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400" htmlFor="p-msg">Tell us more *</label>
                    <textarea id="p-msg" data-testid="partnership-message-input" required rows={5} value={form.message} onChange={set("message")} className="input-dark resize-none" placeholder="Preferred dates, online or on-campus, student nationalities/visa mix, and any budget guidance." />
                  </div>
                  <button type="submit" disabled={sending} className="btn-gold disabled:opacity-60" data-testid="partnership-submit-btn">
                    {sending ? "Sending…" : "Request Quote"} <Send size={15} />
                  </button>
                </form>
              </>
            )}
          </div>
        </Reveal>
      </section>
    </div>
  );
}
